"use client";

import type { FaqItem } from "@/data/faqs";
import { FaqClientWrapper } from "./FaqClientWrapper";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export type FAQVariant = 'default' | 'simple' | 'with-search';

export interface FAQProps {
  faqs: FaqItem[];
  variant?: FAQVariant;
  title?: string;
  subtitle?: string;
  className?: string;
  showSearch?: boolean;
  sectionClassName?: string;
}

/**
 * Unified FAQ component that can be used throughout the application.
 * Supports multiple display variants for client-side rendering.
 * For server-side rendering with SEO optimization, use FaqServerContent directly.
 * 
 * @param faqs - Array of FAQ items to display
 * @param variant - Display variant: 'default' (with search), 'simple' (accordion only), 'with-search' (explicit search)
 * @param title - Optional title for the FAQ section
 * @param subtitle - Optional subtitle for the FAQ section
 * @param className - Additional CSS classes
 * @param showSearch - Explicitly control search visibility (overrides variant)
 * @param sectionClassName - Additional CSS classes for the section wrapper
 */
export function FAQ({
  faqs,
  variant = 'simple',
  title,
  subtitle,
  className,
  showSearch,
  sectionClassName,
}: FAQProps) {
  // Determine if search should be shown
  const shouldShowSearch = showSearch !== undefined 
    ? showSearch 
    : variant === 'with-search' || variant === 'default';

  // Client-side rendering with search
  if (shouldShowSearch) {
    return (
      <section className={sectionClassName}>
        {(title || subtitle) && (
          <div className="text-left mb-8 md:mb-12">
            {title && (
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-base md:text-lg text-muted-foreground">
                {subtitle}
              </p>
            )}
          </div>
        )}
        <FaqClientWrapper faqs={faqs} />
      </section>
    );
  }

  // Client-side rendering without search (simple accordion)
  return (
    <section className={sectionClassName}>
      {(title || subtitle) && (
        <div className="text-left mb-8 md:mb-12">
          {title && (
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-base md:text-lg text-muted-foreground">
              {subtitle}
            </p>
          )}
        </div>
      )}
      <div className={className}>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
            >
              <AccordionTrigger className="text-left">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
